import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, CreditCard, ArrowUpDown } from "lucide-react";
import { toast } from "react-toastify";
import SearchBar from "../../components/SearchBar";
import { formatCurrencyCOP } from "../../utils/format";
import Pagination from "../../components/Pagination";
import LoadingOverlay from "../../components/LoadingOverlay";
import Select from "react-select";


function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]); // ✅ Estado para categorías
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all"); // ✅ Estado filtro categoría
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  const itemsPerPage = 50;
  const [currentPage, setCurrentPage] = useState(0);

  // Debounce para búsqueda
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Resetear página cuando cambie cualquier filtro
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, supplierFilter, categoryFilter, statusFilter, dateFrom, dateTo, sortOrder]);

  // Cargar facturas cuando cambien dependencias
  useEffect(() => {
    fetchInvoices();
  }, [currentPage, debouncedSearch, supplierFilter, categoryFilter, statusFilter, dateFrom, dateTo, sortOrder]);

  // Cargar datos iniciales
  useEffect(() => {
    fetchSuppliers();
    fetchCategories(); // ✅ Nueva llamada
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const res = await api.get("/invoices", {
        params: {
          page: currentPage + 1,
          limit: itemsPerPage,
          search: debouncedSearch,
          supplierId: supplierFilter,
          supplierCategoryId: categoryFilter, // ✅ Enviado al backend
          status: statusFilter,
          dateFrom,
          dateTo,
          sortOrder,
        },
      });
      setInvoices(res.data.data);
      setTotalItems(res.data.total);
    } catch {
      toast.error("Error al cargar facturas");
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await api.get("/suppliers");
      setSuppliers(res.data);
    } catch {
      toast.error("Error al cargar proveedores");
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get("/supplier-categories"); // ✅ Ajusta a tu endpoint real
      setCategories(res.data);
    } catch {
      toast.error("Error al cargar categorías");
    }
  };

  const handleExportExcel = async () => {
    try {
      const res = await api.get("/export/invoices/excel", {
        params: {
          search: searchTerm,
          supplierId: supplierFilter,
          supplierCategoryId: categoryFilter, // ✅ Incluido en Excel
          status: statusFilter,
          dateFrom,
          dateTo,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "facturas.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("No se pudo exportar el archivo");
    }
  };

  const pageCount = Math.ceil(totalItems / itemsPerPage);

  const supplierFilterOptions = [
    { value: "all", label: "-Todos los Proveedores" },
    ...suppliers.map((s) => ({
      value: s.id,
      label: s.name,
    })),
  ];

  return (
    <div className="bg-white p-6 rounded-xl shadow-md relative">
      <LoadingOverlay loading={loading} />

      {/* ✅ Cabecera Superior: Pestañas a la izquierda, Acciones a la derecha */}
      <div className="flex justify-between items-center mb-6">
        {/* Título de la vista (Ocupa el lugar de las pestañas para mantener el equilibrio) */}
        <h2 className="text-3xl font-semibold text-gray-800">Facturas</h2>

        {/* Grupo de Acciones (Se mantienen a la derecha gracias al justify-between) */}
        <div className="flex gap-3 items-center">
          {/* Botón Excel */}
          <button
            onClick={handleExportExcel}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow transition flex items-center gap-2"
          >
            <span>Excel</span>
          </button>

          {/* Botón Nueva Factura */}
          <Link
            to="/invoices/create"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow flex items-center gap-2 transition whitespace-nowrap"
          >
            <Plus size={18} />
            <span>Nueva Factura</span>
          </Link>
        </div>
      </div>

      {/* ✅ Filtros (Ahora sin el botón de Excel) */}
      <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
        <h2 className="text-3xl font-semibold text-gray-800"></h2>

        <div className="flex gap-3 items-center">
          {/* Contenedor Búsqueda + Orden */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg pr-2">
            <SearchBar value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />

            {/* 🔄 Botón de Ordenar */}
            <button
              onClick={() => setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC")}
              title={sortOrder === "ASC" ? "Ver más recientes primero" : "Ver más antiguas primero"}
              className={`p-2 rounded-md transition ${sortOrder === "DESC" ? "bg-blue-100 text-blue-600" : "text-gray-500 hover:bg-gray-200"}`}
            >
              <ArrowUpDown size={20} />
            </button>
          </div>

          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border rounded p-2"
          />

          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border rounded p-2"
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded p-2"
          >
            <option value="all">Estado</option>
            <option value="pending">Pendiente</option>
            <option value="partial">Abono</option>
            <option value="paid">Pagada</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setSupplierFilter("all"); // Opcional: Resetear proveedor al cambiar categoría
            }}
            className="border rounded p-2 bg-white"
          >
            <option value="all">Todas las Categorías</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>

          <Select
            options={supplierFilterOptions}
            placeholder="Seleccionar proveedor..."
            value={supplierFilterOptions.find(o => o.value === supplierFilter) || supplierFilterOptions}
            onChange={(option) => setSupplierFilter(option?.value || "all")}
            className="min-w-[350px]"
            classNamePrefix="react-select"
            isSearchable={true}
          />
        </div>
      </div>

      {/* ✅ Tabla */}
      <div className="overflow-x-auto rounded-xl shadow">
        <table className="min-w-full bg-white table-auto">
          <thead>
            <tr className="bg-gray-100 text-gray-700 text-center text-sm uppercase tracking-wider">
              <th className="px-6 py-3">Proveedor</th>
              <th className="px-6 py-3">N° Factura</th>
              <th className="px-6 py-3">Fecha</th>
              <th className="px-6 py-3">Vencimiento</th>
              <th className="px-6 py-3">Facturado</th>
              <th className="px-6 py-3">Pagado</th>
              <th className="px-6 py-3">Saldo</th>
              <th className="px-6 py-3">Acciones</th>
            </tr>
          </thead>

          <tbody className="text-center">
            {invoices.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-6 text-gray-400 italic">
                  No hay registros.
                </td>
              </tr>
            ) : (
              invoices.map((invoice, index) => (
                <tr
                  key={invoice.id}
                  className={`border-t ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                >
                  <td className="px-6 py-4">{invoice.supplier?.name}</td>
                  <td className="px-6 py-4">{invoice.invoice_number}</td>
                  <td className="px-6 py-4">
                    {invoice.date ? invoice.date.split("-").reverse().join("/") : "-"}
                  </td>
                  <td className="px-6 py-4">
                    {invoice.due_date ? invoice.due_date.split("-").reverse().join("/") : "-"}
                  </td>


                  <td className="px-6 py-4">{formatCurrencyCOP(invoice.total)}</td>

                  <td
                    className={`px-6 py-4 font-semibold ${invoice.paid === 0
                        ? "text-red-600"
                        : invoice.balance > 0
                          ? "text-yellow-600"
                          : "text-green-600"
                      }`}
                  >
                    <div className="flex flex-col">
                      <span>{formatCurrencyCOP(invoice.paid)}</span>

                      {invoice.paid > 0 && invoice.lastPaymentDate && (
                        <span className="text-xs text-gray-500 font-normal mt-1">
                          {invoice.lastPaymentDate
                            .split("-")
                            .reverse()
                            .join("/")}
                        </span>
                      )}
                    </div>
                  </td>

                  <td
                    className={`px-6 py-4 font-bold ${invoice.balance > 0 ? "text-red-600" : "text-green-600"
                      }`}
                  >
                    {formatCurrencyCOP(invoice.balance)}
                  </td>

                  <td className="px-6 py-4">
                    <div className="flex justify-center gap-2">
                      <Link
                        to={`/invoices/${invoice.id}/payments`}
                        className="bg-green-600 hover:bg-green-700 text-white p-2 rounded-lg shadow transition"
                      >
                        <CreditCard size={16} />
                      </Link>
                      <Link
                        to={`/invoices/edit/${invoice.id}`}
                        className="bg-yellow-400 hover:bg-yellow-500 text-white p-2 rounded-lg shadow transition"
                      >
                        <Pencil size={16} />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            currentItemsCount={invoices.length}
            totalItemsCount={totalItems}
          />
        )}
      </div>
    </div>
  );
}

export default Invoices;
